import { apiPost } from './apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../config/apiEndpoints';

interface GenerateKeyResponse {
  result: string; // The plaintext API key (shown once)
}

/**
 * Generate a new MCP API key for the tenant.
 * The key is returned only once — store it immediately.
 * @param tenantId — the tenant's numeric ID
 * @returns the plaintext API key (format: notea_mcp_<128-char hex>)
 */
export const generateMcpApiKey = async (tenantId: number): Promise<string> => {
  const response = await apiPost(buildApiUrl(API_ENDPOINTS.mcp.generateKey(tenantId)), {});
  const data: GenerateKeyResponse = await response.json();
  return data.result;
};

/**
 * Revoke the current MCP API key for the tenant.
 * After calling this, the old key becomes invalid immediately.
 * @param tenantId — the tenant's numeric ID
 */
export const revokeMcpApiKey = async (tenantId: number): Promise<void> => {
  await apiPost(buildApiUrl(API_ENDPOINTS.mcp.revokeKey(tenantId)), {});
};

/**
 * Generate a short-lived OAuth authorization code for the tenant.
 * This is used by the authorization-code-grant flow: the code is placed
 * in the redirect URI, and the OAuth client exchanges it for a real API
 * key via POST /oauth/token.
 * @param tenantId — the tenant's numeric ID
 * @returns a one-time code valid for 60 seconds (format: notea_oauth_<hex>)
 */
export const generateMcpOAuthCode = async (tenantId: number): Promise<string> => {
  const response = await apiPost(buildApiUrl(API_ENDPOINTS.mcp.generateOAuthCode(tenantId)), {});
  const data: { result: string } = await response.json();
  return data.result;
};
